import { DynamicRange, Resolution } from '@tv2-player/unifiedplayer/src/types/BaseTypes';
import { CastDeviceVersion } from '@tv2-player/unifiedplayer/src/types/CastDeviceVersion';
import { getMediaCapabilities } from '@tv2-player/unifiedplayer/src/modules/mediaCapabilities';
import cast from '../types/chromecast-caf-receiver';

const context = cast.framework.CastReceiverContext?.getInstance();

/**
 * Detects the device type and returns the device type as a string.
 * @returns {CastDeviceVersion} The version of the chromecast device.
 */
export const deviceDetector = (): CastDeviceVersion => {
  try {
    if (isDeviceWithGoogleTV() && canDeviceDisplayH265inUHD())
      return CastDeviceVersion.GOOGLE_TV_4k;
    if (isDeviceWithGoogleTV()) return CastDeviceVersion.GOOGLE_TV;
    if (canDeviceDisplayH265inUHD()) return CastDeviceVersion.ULTRA_4k;
    if (isDevice3rdGen()) return CastDeviceVersion.GEN3;
    if (isDevice2ndGen()) return CastDeviceVersion.GEN2;
    if (isDevice1stGen()) return CastDeviceVersion.GEN1;
    if (isDeviceNestHub()) return CastDeviceVersion.NEST_HUB;
    if (isDeviceNestHubMax()) return CastDeviceVersion.NEST_HUB_MAX;

    return CastDeviceVersion.UNKNOWN;
  } catch {
    return CastDeviceVersion.DETECTION_FAILED;
  }
};

const supported720PWidth = 1280;
const supported720PHeight = 720;
const supported1080PWidth = 1920;
const supported1080PHeight = 1080;
const supported2160PWidth = 3840;
const supported2160pHeight = 2160;
const mp4 = 'video/mp4';
const codecH264Lvl4Dot1 = 'avc1.640028';
const codecH264Lvl4Dot2 = 'avc1.64002A';
const codecH265 = 'hev1.1.6.L150.B0';

function isDeviceWithGoogleTV(): boolean {
  return context.getDeviceCapabilities().remote_control_input_supported;
}

// https://developers.google.com/cast/docs/media#audio_passthrough we use codec because each device has a limitation so makes this as accurate as it can be
export function canDeviceDisplayH265inUHD(): boolean {
  return context.canDisplayType(mp4, codecH265, supported2160PWidth, supported2160pHeight, 30);
}

export function isDevice3rdGen(): boolean {
  if (canDeviceDisplayH265inUHD()) return false;

  return context.canDisplayType(
    mp4,
    codecH264Lvl4Dot2,
    supported1080PWidth,
    supported1080PHeight,
    60
  );
}

function isDevice2ndGen(): boolean {
  if (isDevice3rdGen()) return false;

  return context.canDisplayType(
    mp4,
    codecH264Lvl4Dot1,
    supported1080PWidth,
    supported1080PHeight,
    30
  );
}

function isDevice1stGen(): boolean {
  if (isDevice2ndGen()) return false;

  return context.canDisplayType(mp4, codecH264Lvl4Dot1, supported1080PWidth, supported1080PHeight);
}

function isDeviceNestHub(): boolean {
  if (isDevice1stGen()) return false;

  return context.canDisplayType(
    mp4,
    codecH264Lvl4Dot1,
    supported720PWidth,
    supported720PHeight,
    60
  );
}

function isDeviceNestHubMax(): boolean {
  if (isDeviceNestHub()) return false;

  return context.canDisplayType(
    mp4,
    codecH264Lvl4Dot1,
    supported720PWidth,
    supported720PHeight,
    30
  );
}

export const getChromecastSupportedResolutions = async () => {
  if (canDeviceDisplayH265inUHD()) {
    return [Resolution.R_4K, Resolution.R_1080P, Resolution.R_720P];
  }
  return [Resolution.R_1080P, Resolution.R_720P];
};
export const getChromecastSupportedDynamicRange = async () => {
  const instance = cast.framework.CastReceiverContext.getInstance();
  const capabilities = instance.getDeviceCapabilities();

  if (capabilities.is_hdr_supported) {
    return [DynamicRange.HDR, DynamicRange.SDR];
  }
  return [DynamicRange.SDR];
};

/**
 * Special version of getMediaCapabilities that sends the cast version in
 * options and uses special checkers for things that is detected differently
 * on chromecast
 */
export function getChromecastMediaCapabilities() {
  const castDeviceVersion = deviceDetector();
  return getMediaCapabilities(
    { castDeviceVersion },
    {
      getSupportedResolutions: getChromecastSupportedResolutions,
      getSupportedDynamicRange: getChromecastSupportedDynamicRange,
    }
  );
}
